import { useRef, useCallback, useState } from 'react';
import { useFlowchartInteraction } from './useFlowchartInteraction';
import { useFlowchartLayout } from './useFlowchartLayout';
import { useConnectionHandler } from './useConnectionHandler';
import { useAutoPanAfterDelete } from './useAutoPanAfterDelete';
import { useFlowchartState } from './useFlowchartState';

/**
 * Local flowchart state, ELK layout, connection handler, auto-pan stubs, interaction, canvas menu.
 */
export function useFlowchartCoreBase({
  tasks,
  autoArrange,
  campaignId,
  isMultiCampaignMode,
  chainOrder,
  trackpadPanSensitivity,
  trackpadZoomSensitivity,
  onDependencyChange,
  onConditionsChange,
  onPositionChange,
  onPositionChangeBatch,
  onDeleteTasks,
  hasActiveFilters,
  onChainTouched,
  campaignName = '',
}) {
  const state = useFlowchartState();
  const { nodeDimensions, snapToGrid, setSnapToGrid, handleDimensionsChange } = state;

  const effectivePositionsRef = useRef(null);
  const layoutCustomPositionsRef = useRef({});
  const clearCustomPositionsBeforeElkApplyRef = useRef(() => {});

  const setPanAfterConnectRef = useRef(null);
  const connectLayoutCenterRef = useRef(null);
  const pendingCenterTaskIdRef = useRef(null);

  const layout = useFlowchartLayout({
    tasks,
    nodeDimensions,
    autoArrange,
    isMultiCampaignMode,
    chainOrder,
    campaignId,
    customPositionsRef: layoutCustomPositionsRef,
    clearCustomPositionsBeforeElkApplyRef,
    connectLayoutCenterRef,
    pendingCenterTaskIdRef,
    hasActiveFilters,
  });

  const {
    autoPositions,
    chainInfo,
    elkEdgePaths,
    elkPositionsOk,
    measuredElkSettled,
    isAnimatingLayout,
    isAnimatedTidyToolbarSession,
    layoutVersion,
    committedElkLayoutVersion,
    triggerLayout,
    triggerLayoutInstant,
    useAutoPositions,
    taskIdSignature,
    dependencySignature,
    beginLayoutHold,
    layoutHoldRef,
  } = layout;

  const elkPositionsOkRef = useRef(elkPositionsOk);
  elkPositionsOkRef.current = elkPositionsOk;

  const clearCustomPositionsForNodeIdsRef = useRef(null);

  const { handleConnectionComplete, scheduleGraphTidy, setAutoLayoutHandler, followTaskAfterLayout } = useConnectionHandler({
    tasks,
    autoArrange,
    isMultiCampaignMode,
    clearCustomPositionsForNodeIds: (ids) => clearCustomPositionsForNodeIdsRef.current?.(ids),
    setPanAfterConnectRef,
    connectLayoutCenterRef,
    pendingCenterTaskIdRef,
    beginLayoutHold,
  });

  const autoPan = useAutoPanAfterDelete({
    tasks,
    nodePositions: autoPositions,
    nodeDimensions,
    pan: { x: 0, y: 0 },
    zoom: 1,
    containerRef: { current: null },
    setPanAnimated: () => {},
  });

  const afterDeleteHandlerRef = useRef(autoPan.handleAfterDelete);

  const handleAfterDeleteStable = useCallback((deletedIds) => {
    afterDeleteHandlerRef.current?.(deletedIds);
  }, []);

  const [canvasContextMenuState, setCanvasContextMenuState] = useState(null);
  const canvasMenuRef = useRef(null);

  // Unhide the menu that is already in the page. Setting state here would
  // re-render the flowchart before the browser can paint.
  const handleCanvasContextMenuOpen = useCallback(({ screenPosition, canvasPosition }) => {
    canvasMenuRef.current?.reveal(screenPosition, canvasPosition);
  }, []);

  const dismissCanvasContextMenu = useCallback(() => {
    canvasMenuRef.current?.dismiss();
  }, []);

  const interaction = useFlowchartInteraction({
    tasks,
    nodePositions: autoPositions,
    nodeDimensions,
    onDependencyChange,
    onConditionsChange,
    onPositionChange,
    onPositionChangeBatch,
    onDeleteTasks,
    snapToGrid,
    autoArrange,
    trackpadPanSensitivity,
    trackpadZoomSensitivity,
    isMultiCampaignMode,
    effectivePositionsRef,
    onConnectionComplete: handleConnectionComplete,
    onEdgeDeleted: scheduleGraphTidy,
    onAfterDelete: handleAfterDeleteStable,
    onCanvasContextMenu: handleCanvasContextMenuOpen,
    onDismissCanvasContextMenu: dismissCanvasContextMenu,
    hasActiveFilters,
    onChainTouched,
    campaignId,
    campaignName,
  });

  setPanAfterConnectRef.current = interaction.setPanUnconstrained;

  layoutCustomPositionsRef.current = interaction.customPositions || {};
  clearCustomPositionsBeforeElkApplyRef.current = (elkPositions) => {
    if (!elkPositions || typeof elkPositions !== 'object') return;
    interaction.setCustomPositions((prev) => {
      if (!prev || typeof prev !== 'object') return prev;
      let changed = false;
      const next = { ...prev };
      // Drop every override ELK just placed. Keeping stale customs (old x/y)
      // after a delete/connect FLIP is what snaps nodes back, then forward.
      for (const id of Object.keys(elkPositions)) {
        if (next[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const clearCustomPositionsForNodeIds = useCallback(
    (ids) => {
      if (!ids) return;
      const idList = ids instanceof Set ? [...ids] : Array.isArray(ids) ? ids : [];
      if (idList.length === 0) return;
      interaction.setCustomPositions((prev) => {
        const next = { ...prev };
        idList.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    },
    [interaction.setCustomPositions],
  );
  clearCustomPositionsForNodeIdsRef.current = clearCustomPositionsForNodeIds;

  return {
    nodeDimensions,
    snapToGrid,
    setSnapToGrid,
    handleDimensionsChange,
    effectivePositionsRef,
    layoutCustomPositionsRef,
    autoPositions,
    chainInfo,
    elkEdgePaths,
    elkPositionsOk,
    measuredElkSettled,
    isAnimatingLayout,
    isAnimatedTidyToolbarSession,
    triggerLayout,
    triggerLayoutInstant,
    useAutoPositions,
    elkPositionsOkRef,
    layoutVersion,
    committedElkLayoutVersion,
    taskIdSignature,
    dependencySignature,
    handleConnectionComplete,
    followTaskAfterLayout,
    setAutoLayoutHandler,
    autoPan,
    afterDeleteHandlerRef,
    handleAfterDeleteStable,
    canvasContextMenuState,
    setCanvasContextMenuState,
    canvasMenuRef,
    interaction,
    clearCustomPositionsForNodeIds,
    layoutHoldRef,
  };
}
